import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "./../../config/axiosConfig";
import config from "../../config/api";
import { setRealtimeEvent } from "../../realtime/realtimeSlice";
import {
  addItemToList,
  getRealtimeEntityFromPayload,
  isModuleMatch,
  removeItemFromList,
  updateItemInList,
} from "../../realtime/realtimeUtils";
import {
  filterLeadsHiddenInCrm,
  isCrmSubscriptionPackage,
} from "../../utils/crmHiddenLeadsUtils";

const BackendURL = config.backendUrl;
const API_URL = `${BackendURL}/employees`;

const ongoingRequests = {};

const resolveEntityId = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    const id = value._id ?? value.id;
    return id != null ? String(id) : "";
  }
  return String(value);
};

const getLeadEntityId = (lead) => resolveEntityId(lead?._id ?? lead?.id);

const getLeadGroupId = (lead) =>
  resolveEntityId(
    lead?.AssignedGroupId ||
      lead?.AssignedGroupID ||
      lead?.groupid ||
      lead?.groupId
  );

const leadMatchesEmpGroups = (lead, empGroups, empGroupNames) => {
  const leadGroupId = getLeadGroupId(lead);
  const inGroupById =
    leadGroupId && empGroups.some((groupId) => String(groupId) === leadGroupId);
  const assignedGroupId = resolveEntityId(lead?.AssignedGroup);
  const inGroupByAssignedId =
    assignedGroupId && empGroups.some((groupId) => String(groupId) === assignedGroupId);
  const inGroupByName =
    Array.isArray(empGroupNames) &&
    lead?.AssignedGroup &&
    empGroupNames.includes(lead.AssignedGroup);
  return inGroupById || inGroupByAssignedId || inGroupByName;
};

/** L3 (salesperson): leads must match EmpGroups AND createdBy (both required). */
const filterLeadsForAuthLevel = (allLeads, authLevel, empGroups, empGroupNames, loggedInUserId) => {
  // Auth not loaded yet — never expose unfiltered leads (prevents company-wide flash for L3)
  if (authLevel == null) {
    return [];
  }
  if (authLevel !== "L3") {
    return allLeads;
  }
  if (!Array.isArray(empGroups)) {
    return [];
  }
  const userId = resolveEntityId(loggedInUserId);
  if (!userId) {
    return [];
  }
  return (allLeads || []).filter((lead) => {
    const inGroup = leadMatchesEmpGroups(lead, empGroups, empGroupNames);
    const createdByMatch = resolveEntityId(lead?.createdBy) === userId;
    return inGroup && createdByMatch;
  });
};

const syncFilteredLeads = (state) => {
  state.leads = filterLeadsForAuthLevel(
    state.allLeads,
    state.authLevel,
    state.empGroups,
    state.empGroupNames,
    state.loggedInUserId
  );
};

const getCrmPackageFromState = (getState) => {
  const profiles = getState()?.companyProfile?.profiles;
  return isCrmSubscriptionPackage(profiles?.[0]?.subscriptionpackage);
};

export const fetchLeads = createAsyncThunk(
  "leads/fetchLeads",
  async (_, { getState, rejectWithValue }) => {
    const { leads } = getState();

    if (leads.lastFetchTime && Date.now() - leads.lastFetchTime < 5000) {
      return leads.allLeads;
    }

    if (ongoingRequests['leads']) {
      return ongoingRequests['leads'];
    }

    try {
      // Cache-bust so Finance (L2) always gets full body; 304 can return empty body and wipe allLeads
      ongoingRequests['leads'] = axiosInstance.get("/leads", {
        params: { _: Date.now() },
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      })
        .then((response) => {
          const data = response.data;
          // Normalize: backend may return array or wrapped { data: [...] } / { leads: [...] }
          let normalized = [];
          if (Array.isArray(data)) normalized = data;
          else if (data && Array.isArray(data.data)) normalized = data.data;
          else if (data && Array.isArray(data.leads)) normalized = data.leads;
          const isCrmPackage = getCrmPackageFromState(getState);
          return filterLeadsHiddenInCrm(normalized, isCrmPackage);
        })
        .catch(error => {
          const errorPayload = error.response?.data || {
            error: error.message || "Failed to fetch leads"
          };
          throw rejectWithValue(errorPayload);
        });

      return await ongoingRequests['leads'];
    } finally {
      delete ongoingRequests['leads'];
    }
  }
);

export const addLead = createAsyncThunk(
  "leads/addLead",
  async (leadData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/leads", leadData);
      return response.data;
    } catch (err) {
      const errorPayload = err.response?.data || {
        error: err.message || "Failed to add lead"
      };
      return rejectWithValue(errorPayload);
    }
  }
);

export const updateLead = createAsyncThunk(
  "leads/updateLead",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      // Send quotationId in req.body instead of ProjectName for backend (except when Status is Pending — then send ProjectName so it can be updated)
      const body = { ...updatedData };
      const isPending = (body.Status || "").toString().toLowerCase() === "pending";
      if (!isPending) {
        if (body.quotationId != null) {
          delete body.ProjectName;
        } else if (body.ProjectName != null) {
          body.quotationId = body.ProjectName;
          delete body.ProjectName;
        }
      }
      const response = await axiosInstance.put(`/leads/${id}`, body);
      return response.data;
    } catch (err) {
      const errorPayload = err.response?.data || {
        error: err.message || "Failed to update lead"
      };
      return rejectWithValue(errorPayload);
    }
  }
);

export const deleteLead = createAsyncThunk(
  "leads/deleteLead",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/leads/${id}`);
      return id;
    } catch (err) {
      const errorPayload = err.response?.data || {
        error: err.message || "Failed to delete lead"
      };
      return rejectWithValue(errorPayload);
    }
  }
);

export const fetchEmployeeAuthData = createAsyncThunk(
  "leads/fetchEmployeeAuthData",
  async (_, { rejectWithValue }) => {
    try {
      const loggedInUserEmail = (localStorage.getItem("name") || "").trim();
      const EmpLastName = (localStorage.getItem("lastName") || "").trim();
      const EmpFullName = `${loggedInUserEmail} ${EmpLastName}`.trim();
      const userRole = localStorage.getItem("userRole") || localStorage.getItem("auth");

      if (!loggedInUserEmail) throw new Error("No user email found");

      const response = await axiosInstance.get(API_URL);
      const employees = response.data;

      const loginEmail = (localStorage.getItem("email") || "").trim().toLowerCase();
      const matchingEmployee =
        employees.find((employee) => {
          const fullName = `${String(employee.EmpFirstName || "").trim()} ${String(employee.EmpLastName || "").trim()}`.trim();
          return fullName === EmpFullName;
        }) ||
        (loginEmail
          ? employees.find(
              (employee) =>
                String(employee.EmpEmail || "").trim().toLowerCase() === loginEmail
            )
          : undefined);

      if (!matchingEmployee) throw new Error("Employee not found");

      const empGroups = Array.isArray(matchingEmployee.EmpGroups) ? matchingEmployee.EmpGroups : [];
      const looksLikeObjectId = (v) => typeof v === "string" && /^[0-9a-fA-F]{24}$/.test(v);
      const empGroupsAreIds = empGroups.length > 0 && empGroups.every(looksLikeObjectId);

      // Backward/forward compatible:
      // - If EmpGroups are IDs, also derive group names so legacy lead data using AssignedGroup (name) can still be filtered.
      let empGroupNames = [];
      if (empGroupsAreIds) {
        try {
          const groupsResp = await axiosInstance.get("/groups");
          const allGroups = Array.isArray(groupsResp.data) ? groupsResp.data : [];
          const idToName = new Map();
          allGroups.forEach((g) => {
            const id = g?._id || g?.id;
            const name = g?.newGroupName;
            if (id && name) idToName.set(String(id), name);
          });
          empGroupNames = empGroups
            .map((id) => idToName.get(String(id)))
            .filter(Boolean);
        } catch {
          empGroupNames = [];
        }
      }

      return {
        empGroups,
        empGroupNames,
        authLevel: userRole,
        loggedInUserId: resolveEntityId(matchingEmployee._id || matchingEmployee.id),
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateQuotedExpense = createAsyncThunk(
  "leads/updateQuotedExpense",
  async ({ id, quotedExpense }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/leads/update-quoted-expense/${id}`, { quotedExpense });
      return response.data.lead; // ✅ only return updated lead object
    } catch (err) {
      const errorPayload = err.response?.data || {
        error: err.message || "Failed to update quotedExpense"
      };
      return rejectWithValue(errorPayload);
    }
  }
);


const leadSlice = createSlice({
  name: "leads",
  initialState: {
    leads: [],
    allLeads: [],
    empGroups: [],
    empGroupNames: [],
    loggedInUserId: null,
    authLevel: null,
    loading: false,
    error: null,
    isDataFetched: false,
    lastFetchTime: null,
    isFetching: false,
  },
  reducers: {
    logout: (state) => {
      state.leads = [];
      state.allLeads = [];
      state.empGroups = [];
      state.empGroupNames = [];
      state.loggedInUserId = null;
      state.authLevel = null;
      state.loading = false;
      state.error = null;
      state.isDataFetched = false;
      state.lastFetchTime = null;
      state.isFetching = false;
    },
    setEmpGroups: (state, action) => {
      state.empGroups = action.payload;
    },
    invalidateLeadsCache: (state) => {
      state.lastFetchTime = null;
      state.isDataFetched = false;
    },
    filterLeadsByGroup: (state) => {
      syncFilteredLeads(state);
    },
    resetLeadsError: (state) => {
      state.error = null;
    },
    removeLeadsHiddenInCrm: (state, action) => {
      const ids = new Set((action.payload || []).map(String));
      if (!ids.size) return;
      state.allLeads = state.allLeads.filter(
        (lead) => !ids.has(getLeadEntityId(lead))
      );
      syncFilteredLeads(state);
      state.lastFetchTime = null;
    },
  },
  extraReducers: (builder) => {
    builder

    .addCase(fetchLeads.pending, (state) => {
        if (!state.isFetching) {
          state.loading = true;
          state.error = null;
          state.isFetching = true;
        }
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.isFetching = false;

        const payload = action.payload;
        const nextAllLeads = Array.isArray(payload)
          ? payload
          : (payload?.data && Array.isArray(payload.data))
            ? payload.data
            : (payload?.leads && Array.isArray(payload.leads))
              ? payload.leads
              : [];

        // 304 or empty response must not wipe existing data (e.g. Finance L2 P&L table)
        const keepExisting = nextAllLeads.length === 0 && state.allLeads.length > 0;
        const effectiveLeads = keepExisting ? state.allLeads : nextAllLeads;

        if (!state.isDataFetched ||
          JSON.stringify(state.allLeads) !== JSON.stringify(effectiveLeads)) {
          state.allLeads = effectiveLeads;
          if (!keepExisting) {
            state.lastFetchTime = Date.now();
            state.isDataFetched = true;
          }

        }
        syncFilteredLeads(state);
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = false;
        state.isFetching = false;
        state.error = action.payload?.error || action.error.message;
      })

      .addCase(addLead.fulfilled, (state, action) => {
        state.allLeads.unshift(action.payload);
        syncFilteredLeads(state);
        state.lastFetchTime = null;
      })
      .addCase(addLead.rejected, (state, action) => {
        state.error = action.payload?.error || action.error.message;
      })

      .addCase(updateLead.fulfilled, (state, action) => {
        const updateLeadInArray = (arr) => {
          const index = arr.findIndex(lead => lead.id === action.payload.id);
          if (index !== -1) {
            arr[index] = action.payload;
          }
        };
        updateLeadInArray(state.allLeads);
        syncFilteredLeads(state);
        state.lastFetchTime = null;
      })
      .addCase(updateLead.rejected, (state, action) => {
        state.error = action.payload?.error || action.error.message;
      })

      .addCase(deleteLead.fulfilled, (state, action) => {
        const deletedId = String(action.payload);
        state.allLeads = state.allLeads.filter(
          (lead) => getLeadEntityId(lead) !== deletedId
        );
        syncFilteredLeads(state);
        state.lastFetchTime = null;
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.error = action.payload?.error || action.error.message;
      })

      .addCase(fetchEmployeeAuthData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeAuthData.fulfilled, (state, action) => {
        state.loading = false;
        state.empGroups = action.payload.empGroups;
        state.empGroupNames = action.payload.empGroupNames || [];
        state.loggedInUserId = action.payload.loggedInUserId ?? null;
        state.authLevel = action.payload.authLevel;
        syncFilteredLeads(state);
      })
      .addCase(fetchEmployeeAuthData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        // Keep L3 gated (empty until auth succeeds). For L0/L1/L2, fall back to
        // localStorage role so dashboard is not stuck blank when employee match fails.
        const role = localStorage.getItem("userRole") || localStorage.getItem("auth");
        if (role && role !== "L3") {
          state.authLevel = role;
          syncFilteredLeads(state);
        }
      })

      .addCase(updateQuotedExpense.fulfilled, (state, action) => {
        const updatedLead = action.payload;
        const updateLeadInArray = (arr) => {
          const index = arr.findIndex(lead => lead.id === updatedLead.id);
          if (index !== -1) {
            arr[index] = updatedLead;
          }
        };
        updateLeadInArray(state.allLeads);
        syncFilteredLeads(state);
        state.lastFetchTime = null; // invalidate cache
      })
      .addCase(updateQuotedExpense.rejected, (state, action) => {
        state.error = action.payload?.error || action.error.message;
      })
      .addCase(setRealtimeEvent, (state, action) => {
        const { module, action: eventAction, data } = action.payload || {};
        if (!isModuleMatch(module, ["leads", "lead"])) return;

        const incomingItem = getRealtimeEntityFromPayload(data);
        if (!incomingItem) return;

        if (eventAction === "updated") {
          state.allLeads = updateItemInList(state.allLeads, incomingItem);
          syncFilteredLeads(state);
        }

        if (eventAction === "created") {
          state.allLeads = addItemToList(state.allLeads, incomingItem);
          syncFilteredLeads(state);
        }

        if (eventAction === "deleted") {
          state.allLeads = removeItemFromList(state.allLeads, incomingItem);
          syncFilteredLeads(state);
        }
      });
  },
});

export const {
  logout,
  setEmpGroups,
  invalidateLeadsCache,
  filterLeadsByGroup,
  resetLeadsError,
  removeLeadsHiddenInCrm,
} = leadSlice.actions;

export const selectFilteredLeads = (state) => state.leads.leads;
export const selectAllLeads = (state) => state.leads.allLeads;
export const selectLeadsLoading = (state) => state.leads.loading;
export const selectLeadsError = (state) => state.leads.error;
export const selectAuthLevel = (state) => state.leads.authLevel;

export default leadSlice.reducer;